import { PiArrowUpFill } from "react-icons/pi";
import { BsSliders } from "react-icons/bs";
import { LuSearch } from "react-icons/lu";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import {
  fetchOrders,
  getOrderChart,
  getOrderSummary,
  getOrderTopCategories,
} from "../../api/order";
import OrderTable from "./OrderTable";
import { Line } from "react-chartjs-2";
import Loading from "../shared/Loading";

const boxShadowStyle = {
  boxShadow: "0px 2px 9px 0px #00000008",
  userSelect: "none",
};

const Orders = () => {
  const { data, isLoading } = useQuery("orders", fetchOrders);
  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    "orders-summary",
    getOrderSummary
  );
  const { data: chartData, isLoading: chartLoading } = useQuery(
    "orders-chart",
    getOrderChart
  );
  const { data: categoryData } = useQuery(
    "orders-categories",
    getOrderTopCategories
  );

  return (
    <>
      {isLoading || summaryLoading || chartLoading ? (
        <Loading />
      ) : (
        <div className="w-full h-full pt-5 px-5 pb-40 space-y-6">
          <div className="flex flex-col gap-5">
            <div className="font-dmsans font-bold text-[22px] text-[#070E05]">
              Orders
            </div>
            {/* Summary */}
            <div className="flex gap-5">
              <div className="flex whitespace-nowrap overflow-x-auto w-full gap-4">
                <div className="w-full p-4 bg-white rounded-lg font-Neurial flex flex-col gap-3">
                  <div className="font-bold text-[15px]">Total Orders</div>
                  <p>
                    {Number(summaryData?.totalOrders || 0).toLocaleString()}
                  </p>
                </div>

                <div className="w-full p-4 bg-white rounded-lg font-Neurial flex flex-col gap-3">
                  <div className="font-bold text-[15px]">Completed Orders</div>
                  <p>
                    {Number(summaryData?.completedOrders || 0).toLocaleString()}
                  </p>
                </div>

                <div className="w-full p-4 bg-white rounded-lg font-Neurial flex flex-col gap-3">
                  <div className="font-bold text-[15px]">GMV Gross</div>
                  <p>₦ {Number(summaryData?.gmvGross || 0).toLocaleString()}</p>
                </div>

                <div className="w-full p-4 bg-white rounded-lg font-Neurial flex flex-col gap-3">
                  <div className="font-medium text-[14px]">GMV Net</div>
                  <p>₦ {Number(summaryData?.gmvNet || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-4 font-Neurial ">
              <div className="bg-white rounded-lg w-[50%] p-4 flex flex-col">
                <div className="font-medium text-[14px]">Order chart</div>
                <OrderChart series={chartData?.series} />
              </div>
              <div className="w-[50%] bg-white rounded-lg p-4">
                <div className="font-medium text-[14px]">
                  Top Order Category
                </div>
                <div className="mt-7 flex flex-col gap-5">
                  {(categoryData || []).map((v, k) => {
                    return (
                      <Link
                        to={`/products/category/${v}`}
                        className="flex justify-between items-center"
                        key={k}
                      >
                        <div className="text-[14px] text-[#444A6D] font-Poppins">
                          Sweatshirt
                        </div>
                        <div className="text-[#0095FF] rounded-md font-sans border-[1px] border-[#0095FF] bg-[#F0F9FF] text-[13px] px-2 py-1">
                          40%
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 bg-white rounded-lg p-5">
              <div className="flex items-center justify-between">
                <div className="tracking-[-0.4 px] font-dmsans font-bold text-[18px] text-[#070E05]">
                  Recent Orders
                </div>
                <div className="flex items-center gap-[10px]">
                  <div
                    className="border-[1px] border-[#E9EFF4] bg-white rounded-lg py-2 px-2 cursor-pointer"
                    title={`Filter`}
                    style={boxShadowStyle}
                  >
                    <div className="text-[14px] text-[#A0A3BD] leading-5 flex items-center gap-10 w-full h-full">
                      <span>Filter</span>
                      <BsSliders />
                    </div>
                  </div>
                  <div
                    className="border-[1px] border-[#E9EFF4] bg-white rounded-lg py-2 px-2 cursor-pointer"
                    title={`Search`}
                    style={boxShadowStyle}
                  >
                    <div className="text-[14px] text-[#A0A3BD] leading-5 flex items-center gap-2 w-full h-full relative">
                      <LuSearch className="text-[18px]" />
                      <input
                        className="w-[120px] placeholder:text-[#A0A3BD] text-[13px] outline-none text-black bg-transparent"
                        placeholder=" Type to Search"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <OrderTable data={data?.orders || []} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Orders;

export function OrderChart({ series }) {
  const options = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          lineWidth: 0.5,
          tickBorderDash: [1, 10],
        },
        border: {
          dash: [50, 10],
        },
        ticks: {
          align: "end",
          display: true,
          font: {
            size: 11,
          },
          color: "#1818196B",
        },
      },
      y: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          font: {
            size: 11,
          },
          callback: function (val, index) {
            if (index == 0) {
              return 0;
            } else if (index == 3) {
              return 25;
            } else if (index == 6) {
              return 50;
            } else if (index == 9) {
              return 100;
            }
          },
        },
      },
    },
    maintainAspectRatio: false,
  };
  return (
    <div className="bar-container">
      <Line
        data={{
          labels: series?.map((ser) => ser.label) || [],
          datasets: [
            {
              label: "",
              data:
                series?.map((ser) => ser.completedOrders).splice(0, 13) || [],
              borderColor: "#2121F1",
              backgroundColor: "transparent",
              fill: false,
              tension: 0.5,
              pointStyle: false,
              borderWidth: 2,
            },
            {
              label: "",
              data: series?.map((ser) => ser.totalOrders).splice(0, 13) || [],
              borderColor: "#F0D961",
              backgroundColor: "transparent",
              fill: false,
              tension: 0.5,
              pointStyle: false,
              borderWidth: 2,
            },
          ],
        }}
        options={options}
        width={window.innerWidth}
        height={300}
      />
      <div
        className="chart-text"
        style={{
          color: "gray",
          fontSize: "13px",
        }}
      >
        {false ? <div>No data for this chart</div> : <></>}
      </div>
    </div>
  );
}
